import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FaqAPI } from "../api/faq";
import type { Faq } from "../types";

// Query Keys
export const faqKeys = {
  all: ["faqs"] as const,
  lists: () => [...faqKeys.all, "list"] as const,
  list: (clientId: string, filters?: Record<string, unknown>) =>
    [...faqKeys.lists(), clientId, filters] as const,
  details: () => [...faqKeys.all, "detail"] as const,
  detail: (clientId: string, id: string) =>
    [...faqKeys.details(), clientId, id] as const,
};

// List FAQs
export function useFaqs(
  options?: {
    status?: string;
    needsRefinement?: boolean;
  },
  queryOptions?: { enabled?: boolean },
) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<{ faqs: Faq[]; total: number }>({
    queryKey: faqKeys.list(clientId, options),
    queryFn: () => FaqAPI.listFaqs(clientId, options),
    staleTime: 30 * 1000,
    enabled: !!clientId && queryOptions?.enabled !== false,
  });
}

// Create FAQ
export function useCreateFaq() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      question: string;
      refined_response?: string;
      category?: string;
      variations?: string[];
    }) => FaqAPI.createFaq(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: faqKeys.lists() });
    },
  });
}

// Update / refine FAQ
export function useUpdateFaq() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<
        Pick<
          Faq,
          | "question"
          | "refined_response"
          | "category"
          | "variations"
          | "status"
        >
      >;
    }) => FaqAPI.updateFaq(clientId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: faqKeys.lists() });
    },
  });
}

// Detect duplicates
export function useFaqDuplicates(enabled: boolean) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<{ groups: Faq[][]; total: number }>({
    queryKey: [...faqKeys.all, "duplicates", clientId],
    queryFn: () => FaqAPI.listDuplicates(clientId),
    enabled: !!clientId && enabled,
    staleTime: 10 * 1000,
  });
}

// Merge FAQs
export function useMergeFaqs() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      targetId,
      sourceIds,
    }: {
      targetId: string;
      sourceIds: string[];
    }) => FaqAPI.mergeFaqs(clientId, targetId, sourceIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: faqKeys.all });
    },
  });
}

// Delete FAQ
export function useDeleteFaq() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => FaqAPI.deleteFaq(clientId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: faqKeys.lists() });
    },
  });
}
